import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import axios from 'axios';
import config from '../../Config';
import { useDebounce } from 'use-debounce';

import LoadingSpinner from '../../Components/LoadingSpinner';
import FluidMediaModal from '../../Components/FluidMediaModal2';
import CardPack from '../../Components/CardPack';
import FindBar from '../../Components/FindBar';
import Container from '../../Layouts/Container';
import Header from '../../Layouts/Header';
import { useHistory, Link } from "react-router-dom";
import { useAtom } from 'jotai';
import ScrollToTopButton from '../../Components/ScrollToTopButton';
import DarkModeContext from '../../Components/DarkModeContext'; // Import the DarkModeContext
import { authUserAtom, isAuthAtom } from '../../Services/store/atoms';
import AuthRequiredModal from '../../Components/AuthRequiredModal';

function ListPacks(props) {
    const history = useHistory();
    const [authUser] = useAtom(authUserAtom);
    const [isAuth] = useAtom(isAuthAtom);
    const { darkMode } = useContext(DarkModeContext); // Consume the DarkModeContext
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    useBodyScrollLock(showModal);
    const [categories, setCategories] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [gender, setGender] = useState('All');
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [selectedPack, setSelectedPack] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
    const [showAuthModal, setShowAuthModal] = useState(false);

    const [debouncedSearch] = useDebounce(searchValue, 500);
    const fetchAbortRef = useRef(null);

    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (isLoading || !nextPageUrl) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && nextPageUrl) {
                fetchProducts(nextPageUrl, true);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, nextPageUrl]);

    const fetchProducts = useCallback((pageUrl = `${config.REACT_APP_BASE_URL}/api/v1/packs/list`, append = false) => {
        if (!append) {
            if (fetchAbortRef.current) fetchAbortRef.current.abort();
            fetchAbortRef.current = new AbortController();
        }

        setIsLoading(true);

        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            'Accept': 'application/json'
        };
        if (authUser?.token) {
            headers['Authorization'] = `Bearer ${authUser.token}`;
        }

        const requestData = new URLSearchParams();
        if (debouncedSearch && debouncedSearch.trim() !== '') {
            requestData.append('search', debouncedSearch.trim());
        }

        const categoriesValue = categories === 'All Categories' ? '' : categories;
        if (categoriesValue && categoriesValue.trim() !== '') {
            requestData.append('categories', categoriesValue.trim());
        }

        if (gender && gender !== 'All') {
            requestData.append('gender', gender.toLowerCase().trim());
        }

        return axios.post(pageUrl, requestData, {
            headers,
            params: append ? new URL(pageUrl).searchParams : {},
            signal: !append ? fetchAbortRef.current.signal : undefined,
        })
        .then((response) => {
            if (!response.data) throw new Error('La respuesta del servidor está vacía');
            setProducts(prevProducts =>
                append && Array.isArray(response.data.data)
                    ? [...prevProducts, ...response.data.data]
                    : response.data.data || []
            );
            setNextPageUrl(response.data.next_page_url || null);
            return response.data;
        })
        .catch((error) => {
            if (error.code === 'ERR_CANCELED') return;
            console.error('Error al cargar los paquetes:', error.message);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [authUser?.token, categories, debouncedSearch, gender]);

    useEffect(() => {
        fetchProducts(`${config.REACT_APP_BASE_URL}/api/v1/packs/list`, false);
    }, [debouncedSearch, categories, gender, fetchProducts]);

    // Mostrar modal de autenticación cuando la página carga y el usuario no está autenticado
    useEffect(() => {
        if (!isAuth) {
            setShowAuthModal(true);
        }
    }, [isAuth]);

    const handleSearch = (value) => {
        setSearchValue(value);
        // Don't fetch here, let the useEffect handle it
    };

    const handleGenderChange = (selectedGender) => {
        setGender(selectedGender);
        // Don't fetch here, let the useEffect handle it
    };

    const handleCategoriesChange = (selectedCategories) => {
        setCategories(selectedCategories);
        // Don't fetch here, let the useEffect handle it
    };

    const clickPack = (pack) => {
        if (isAuth) {
            setScrollPosition(window.pageYOffset);
            setSelectedPack(pack);
            setShowModal(true);
        } else {
            setShowLoginModal(true);
        }
    };

    const handleBuyPack = () => {
        setShowModal(false);
        history.push(`/user/buyPack/${selectedPack.pack_id}`);
    };

    const handleCancelPurchase = () => {
        setShowModal(false);
        window.scrollTo(0, scrollPosition);
    };

    const handleLoginModalClose = () => {
        setShowLoginModal(false);
        history.push('/login');
    };

    const handleCloseLoginModal = () => {
        setShowLoginModal(false);
    };

    const handleMediaClick = (mediaUrls) => {
        setSelectedMediaUrls(mediaUrls);
        setShowMediaModal(true);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className={`min-h-dvh flex flex-col text-base ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-white text-black'}`} >
            <Header />
            <FindBar
                onSearch={handleSearch}
                onGenderChange={handleGenderChange}
                onCategoriesChange={handleCategoriesChange}
                initialGender={gender}
                hideCategories={true}
                searchValue={searchValue}
            />
            <div className="w-[80%] mx-auto">
                <div className="pt-10">
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-8 gap-x-4 justify-items-center'>
                        {products.map((product, index) => (
                            <div 
                                key={product.pack_id} 
                                ref={products.length === index + 1 ? lastProductElementRef : null}
                                style={{mx:'auto'}}
                            >
                                <CardPack 
                                    pack={product}
                                    onClick={clickPack}
                                    onMediaClick={handleMediaClick}
                                />
                            </div>
                        ))}
                    </div>
                    {isLoading && <LoadingSpinner />}
                </div>
            </div>
            <ScrollToTopButton onClick={scrollToTop} />

            {showModal && ( 
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Do you want to buy this pack?
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-rose-600 text-base font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleBuyPack}
                                >
                                    Buy
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleCancelPurchase}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLoginModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            To make purchases on the page you must be logged in
                                        </h3>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-rose-600 text-base font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleLoginModalClose}
                                >
                                    Loggin
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleCloseLoginModal}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showMediaModal && <FluidMediaModal show={showMediaModal} onClose={() => setShowMediaModal(false)} mediaUrls={selectedMediaUrls} />}
            
            <AuthRequiredModal 
                show={showAuthModal} 
                onClose={() => setShowAuthModal(false)}
                title="Acceso Restringido"
                message="Debes iniciar sesión para ver y comprar packs. Una vez autenticado podrás navegar libremente por la página."
            />
        </div>
    );
}

export default ListPacks;
